'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useRef } from 'react';

import type { PageBlock } from '@/payload/payload-types-block';
import { AccordionItem, UiKitAccordion } from '@/ui-kit/accordion';
import { BreadcrumbsItem, UiKitBreadcrumbs } from '@/ui-kit/breadcrumbs';
import { BaseFieldProps } from '@/ui-kit/field';

import {
  CardParamsPreview,
  CardParamsPreviewFixed,
  DesignSelector,
  GiftCardForm,
  RecipientForm,
  SendDateSelector
} from './components';
import { useFixedBarVisibility } from './hooks';
import { styles } from './UiGiftCardCreate.styles';

type GiftCardCreateDesignCard = NonNullable<
  PageBlock<'giftCardCreate'>['designCards']
>[number];

export interface UiGiftCardCreateProps {
  giftCards: PageBlock<'giftCardCreate'>['designCards'];
  selectedCard: GiftCardCreateDesignCard;
  onChangeSelectedCard: (card: GiftCardCreateDesignCard) => void;
  policyLink: string;
  breadcrumbs: BreadcrumbsItem[];
  faq?: AccordionItem[];
  quantity: number;
  nominal: number;
  onChangeQuantity: (quantity: number) => void;
  onChangeNominal: (nominal: number) => void;
  getFieldProps: (name: string) => BaseFieldProps;
  initialRecipientProfile?: {
    phone?: string;
    email?: string;
  } | null;
  isPayButtonDisabled?: boolean;
  nominalError?: string;
}

export function UiGiftCardCreate({
  giftCards,
  selectedCard,
  onChangeSelectedCard,
  policyLink,
  breadcrumbs,
  faq,
  quantity,
  nominal,
  onChangeQuantity,
  onChangeNominal,
  getFieldProps,
  initialRecipientProfile,
  isPayButtonDisabled,
  nominalError
}: UiGiftCardCreateProps) {
  const slots = styles();
  const t = useTranslations();

  const sentinelRef = useRef<HTMLDivElement>(null);

  const { showFixedBar } = useFixedBarVisibility(
    useCallback(() => sentinelRef.current, [])
  );

  const mainSections = [
    {
      title: t('giftCard.designSelector.title'),
      component: (
        <DesignSelector
          giftCards={giftCards}
          selectedGiftCard={selectedCard}
          onChangeSelectedGiftCard={onChangeSelectedCard}
        />
      )
    },
    {
      title: t('giftCard.form.title'),
      component: (
        <GiftCardForm
          quantity={quantity}
          onChangeQuantity={onChangeQuantity}
          nominal={nominal}
          onChangeNominal={onChangeNominal}
          errorMessage={nominalError}
        />
      )
    },
    {
      title: t('giftCard.recipientForm.title'),
      component: (
        <RecipientForm
          getFieldProps={getFieldProps}
          initialProfile={initialRecipientProfile}
        />
      )
    },
    {
      title: t('giftCard.sendDateSelector.title'),
      component: <SendDateSelector getFieldProps={getFieldProps} />
    }
  ];

  return (
    <div className={slots.base()}>
      <UiKitBreadcrumbs items={breadcrumbs} />
      <div className={slots.layout()}>
        <div className={slots.main()}>
          <h1 className={slots.title()}>{t('giftCard.title')}</h1>
          <div className={slots.sections()}>
            {mainSections.map((section) => (
              <div key={section.title}>
                <h2 className={slots.sectionTitle()}>{section.title}</h2>
                {section.component}
              </div>
            ))}
          </div>
          {!!faq?.length && (
            <div className={slots.faqWrapper()}>
              <h2 className={slots.faqTitle()}>{t('giftCard.faq.title')}</h2>

              <UiKitAccordion
                items={faq}
                className={slots.faqContent()}
                headerClassName={slots.faqContentHeader()}
                contentClassName={slots.faqContentItem()}
              />
            </div>
          )}
        </div>
        <div className={slots.summary()}>
          <div
            ref={sentinelRef}
            className={slots.sentinel()}
            aria-hidden
          />
          <CardParamsPreview
            selectedCard={selectedCard}
            nominal={nominal}
            quantity={quantity}
            policyLink={policyLink}
            isPayButtonDisabled={isPayButtonDisabled}
          />
        </div>
        {showFixedBar && (
          <CardParamsPreviewFixed
            quantity={quantity}
            nominal={nominal}
            isPayButtonDisabled={isPayButtonDisabled}
          />
        )}
      </div>
    </div>
  );
}
